/* ──────────────────────────────────────────────────────────────
   RUN24 — Upsell "Completa tu kit" en el cart drawer.

   Decisión: Product Recommendations API (/recommendations/products.json)
   con el primer item del carrito (intent=related), filtrando productos
   ya presentes en la bolsa. Sin theme setting: cero configuración y las
   recomendaciones siguen al contenido real del carrito.

   No toca cart.js / cart-drawer.js. Como el section rendering de Dawn
   reemplaza el innerHTML de .drawer__inner (o hace replaceWith de
   cart-drawer-items) en cada update, el bloque #CartDrawer-Upsell se
   re-crea vacío; un MutationObserver sobre <cart-drawer> lo re-puebla.

   Alta directa a la bolsa: solo productos de 1 variante (accesorios).
   Con tallas/colores manda a la PDP a elegir.
   ────────────────────────────────────────────────────────────── */

(() => {
  const RECS_LIMIT = 3;
  // Colección curada del kit (calcetas, geles, agujetas, plantillas…).
  // Si no existe o viene vacía, se cae a las recomendaciones relacionadas.
  const KIT_COLLECTION = 'completa-tu-kit';
  let observer = null;
  let refreshTimer = null;
  let inFlight = null;

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // /collections/<handle>/products.json trae otro formato que /recommendations:
  // precio como string en pesos, imágenes en images[], sin url absoluta.
  function normalizeKitProduct(p) {
    const variants = (p.variants || []).map((v) => ({
      id: v.id,
      available: v.available !== false,
    }));
    return {
      id: p.id,
      title: p.title,
      vendor: p.vendor,
      type: p.product_type || '',
      url: `${root()}products/${p.handle}`,
      featured_image: (p.images && p.images[0] && (p.images[0].src || p.images[0])) || null,
      price: p.variants && p.variants[0] ? Math.round(parseFloat(p.variants[0].price) * 100) : 0,
      available: variants.some((v) => v.available),
      variants,
    };
  }

  // Los product_type del catálogo son inconsistentes (Calcetas/Tines/Shoe,
  // Pantorrillera/Pantorilla…) — se agrupan en categorías del kit para que
  // el drawer muestre variedad (gel + calceta + compresión…), no 3 geles.
  function kitGroup(type) {
    const t = (type || '').toLowerCase();
    if (t.includes('gel') || t.includes('chew') || t.includes('ketone')) return 'nutricion';
    if (t.includes('calcet') || t.includes('tines') || t === 'shoe') return 'calcetas';
    if (t.includes('pantor')) return 'compresion';
    if (t.includes('lentes')) return 'lentes';
    if (t.includes('plantilla')) return 'plantillas';
    if (t.includes('playera') || t.includes('short') || t.includes('top')) return 'ropa';
    return t || 'otros';
  }

  // El pool llega en orden best-selling (orden de la colección): la primera
  // vuelta toma el más vendido de cada categoría; si faltan filas, la
  // segunda rellena con los siguientes más vendidos sin repetir producto.
  function diversify(list, limit) {
    const seenGroups = new Set();
    const picked = [];
    // Regla de Diego (2026-09-10): el upsell del carrito siempre arranca con
    // una calceta Feetures disponible (ocupa el slot de su categoría).
    const feetures = list.find((p) => p.available && /feetures/i.test(p.vendor || ''));
    if (feetures && limit > 0) {
      picked.push(feetures);
      seenGroups.add(kitGroup(feetures.type));
    }
    for (const p of list) {
      if (picked.length >= limit) break;
      const g = kitGroup(p.type);
      if (seenGroups.has(g)) continue;
      seenGroups.add(g);
      picked.push(p);
    }
    for (const p of list) {
      if (picked.length >= limit) break;
      if (!picked.includes(p)) picked.push(p);
    }
    return picked;
  }

  function formatMoney(cents) {
    const value = cents / 100;
    const opts =
      value % 1 === 0
        ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
        : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    return '$' + value.toLocaleString('es-MX', opts);
  }

  function escapeHtml(str) {
    return String(str).replace(
      /[&<>"']/g,
      (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );
  }

  function thumbUrl(url) {
    if (!url) return null;
    return url + (url.includes('?') ? '&' : '?') + 'width=112';
  }

  function rowHTML(product) {
    const img = thumbUrl(product.featured_image);
    const singleVariant = Array.isArray(product.variants) && product.variants.length === 1;
    const variant = singleVariant ? product.variants[0] : null;
    const action =
      singleVariant && variant && variant.available
        ? `<button type="button" class="r24-upsell__add" data-r24-upsell-add data-variant-id="${variant.id}">+ Agregar</button>`
        : `<a class="r24-upsell__add" href="${escapeHtml(product.url)}">Elegir</a>`;

    return `
      <div class="r24-upsell__item">
        <a class="r24-upsell__thumb" href="${escapeHtml(product.url)}" tabindex="-1" aria-hidden="true">
          ${
            img
              ? `<img src="${escapeHtml(img)}" alt="" loading="lazy" width="48" height="48">`
              : `<span class="r24-upsell__thumb-letter">${escapeHtml((product.title || '?').slice(0, 1))}</span>`
          }
        </a>
        <div class="r24-upsell__info">
          ${product.vendor ? `<span class="r24-upsell__brand">${escapeHtml(product.vendor)}</span>` : ''}
          <a class="r24-upsell__name" href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a>
          <span class="r24-upsell__price">${formatMoney(product.price)}</span>
        </div>
        ${action}
      </div>`;
  }

  async function refreshUpsell() {
    const upsell = document.getElementById('CartDrawer-Upsell');
    if (!upsell) return;
    const box = upsell.querySelector('[data-upsell-items]');
    if (!box) return;

    try {
      const cart = await fetch(`${window.routes.cart_url}.js`).then((r) => (r.ok ? r.json() : null));
      if (!cart || cart.item_count === 0) {
        upsell.hidden = true;
        return;
      }

      const inCart = new Set(cart.items.map((item) => item.product_id));
      const firstProductId = cart.items[0].product_id;
      const key = `${firstProductId}:${[...inCart].sort().join('-')}`;
      if (upsell.dataset.key === key && box.childElementCount > 0) {
        upsell.hidden = false;
        return;
      }

      let pool = [];
      const kit = await fetch(`${root()}collections/${KIT_COLLECTION}/products.json?limit=24`)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
      if (kit && Array.isArray(kit.products)) pool = kit.products.map(normalizeKitProduct);
      if (!pool.length) {
        const url = `${root()}recommendations/products.json?product_id=${firstProductId}&limit=8&intent=related`;
        const data = await fetch(url).then((r) => (r.ok ? r.json() : null));
        pool = (data && data.products) || [];
      }
      const recs = diversify(
        pool.filter((p) => p.available && !inCart.has(p.id)),
        RECS_LIMIT
      );

      if (!recs.length) {
        upsell.hidden = true;
        return;
      }

      if (observer) observer.disconnect();
      upsell.dataset.key = key;
      box.innerHTML = recs.map(rowHTML).join('');
      upsell.hidden = false;
      observeDrawer();
    } catch (e) {
      upsell.hidden = true;
    }
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      if (inFlight) return;
      inFlight = refreshUpsell().finally(() => {
        inFlight = null;
      });
    }, 150);
  }

  function observeDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    if (!observer) {
      observer = new MutationObserver((mutations) => {
        const relevant = mutations.some((m) => {
          const target = m.target instanceof Element ? m.target : m.target.parentElement;
          return !target || !target.closest('#CartDrawer-Upsell');
        });
        if (relevant) scheduleRefresh();
      });
    }
    observer.observe(drawer, { childList: true, subtree: true });
  }

  /* Alta directa desde la fila (patrón product-form.js de Dawn:
     /cart/add con sections del drawer + renderContents + publish). */
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-r24-upsell-add]');
    if (!btn || btn.disabled) return;
    event.preventDefault();

    btn.disabled = true;
    const originalLabel = btn.textContent;
    btn.textContent = '…';

    const drawer = document.querySelector('cart-drawer');
    const sections = drawer ? drawer.getSectionsToRender().map((s) => s.id) : [];

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({
        items: [{ id: Number(btn.dataset.variantId), quantity: 1 }],
        sections,
        sections_url: window.location.pathname,
      }),
    })
      .then((r) => r.json())
      .then((response) => {
        if (response.status) {
          btn.disabled = false;
          btn.textContent = originalLabel;
          const errors = document.getElementById('CartDrawer-CartErrors');
          if (errors) errors.textContent = response.description || response.message || '';
          return;
        }
        btn.textContent = '✓ Agregado';
        btn.classList.add('r24-upsell__add--added');
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'r24-upsell', cartData: response });
        }
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.renderContents(response);
        }
      })
      .catch(() => {
        btn.disabled = false;
        btn.textContent = originalLabel;
      });
  });

  function init() {
    if (!document.querySelector('cart-drawer')) return;
    observeDrawer();
    scheduleRefresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();